import * as React from 'react';

import { KeytipData } from '@fluentui/react/lib/KeytipData';
import {
  ITextField,
  ITextFieldProps,
  ITextFieldStyleProps,
  ITextFieldStyles,
  TextFieldBase,
} from '@fluentui/react/lib/TextField';
import { KeyCodes, styled } from '@fluentui/react/lib/Utilities';

import { dataUniqueId } from '@1js/acui-common';
import { AppContext } from '@1js/acui-context';
import { checkExperiment, ribbonExperiments } from '@1js/acui-experiments';
import { defaultKeytipExecute, deriveInputKeytip } from '@1js/acui-keytip';
import { AppTooltipHost } from '@1js/acui-tooltip';

import { AppInputProps } from './AppInput.Props';
import { getStyles } from './AppInput.styles';

export interface AppInputState {
  previousValue?: string;
  text?: string;
}

export const AppBaseInput = styled<ITextFieldProps, ITextFieldStyleProps, ITextFieldStyles>(TextFieldBase, getStyles);

export class AppInput extends React.PureComponent<AppInputProps, AppInputState> {
  private textField = React.createRef<ITextField>();
  private tooltipHost: AppTooltipHost | undefined;

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  // VSTS: 3994572 - InputBox: ESC is not cancelling a pending value and is instead submitting the value
  // Member variables needed to restore the original value
  // Reference: https://office.visualstudio.com/OC/_wiki/wikis/OC.wiki/9723/Event-propagation-mixing-React-Synthetic-Events-and-native-events
  private initialText: string | undefined;

  constructor(props: AppInputProps) {
    super(props);

    this.state = {
      previousValue: props.value,
    };
  }

  public render() {
    const {
      placeholder,
      value,
      onRenderSuffix,
      styles,
      spellCheck,
      autoComplete,
      disabled,
      role,
      title,
      label,
      defaultValue,
      multiline,
      resizable,
      maxLength,
      keytipProps,
      readOnly,
    } = this.props;
    let text: string | undefined = '';
    if (defaultValue !== undefined || value === undefined) {
      // if we have a default value, that means the component should be uncontrolled
      text = undefined;
    } else if (this.state.text || this.state.text === '') {
      text = this.state.text;
    } else {
      text = value;
    }

    // Explicitly default to text because if we pass 'undefined' it will be unset.
    // Note: Fabric also sets the default here to text but 'undefined' overrides it.
    const type = this.props.inputType || 'text';

    return (
      <KeytipData keytipProps={deriveInputKeytip(keytipProps, this.keytipExecute)}>
        {(keytipAttributes: any): JSX.Element => (
          <AppBaseInput
            {...keytipAttributes}
            componentRef={this.textField}
            placeholder={placeholder}
            value={text}
            defaultValue={defaultValue}
            onRenderSuffix={onRenderSuffix}
            styles={styles}
            spellCheck={spellCheck ?? false}
            autoComplete={autoComplete}
            onChange={this.onChange}
            onKeyDown={this.onKeyDown}
            role={role}
            aria-setsize={this.props.setSize}
            aria-posinset={this.props.positionInSet}
            title={title}
            disabled={disabled}
            data-unique-id={dataUniqueId(this.props)}
            onFocus={this.focus}
            label={this.props.hideLabel ? undefined : label}
            multiline={multiline}
            resizable={resizable}
            onBlur={this.blur}
            maxLength={maxLength}
            id={this.props.id}
            type={type}
            ariaLabel={this.props.label || this.props.customTooltip}
            readOnly={readOnly || false}
            errorMessage={this.props.errorMessage}
            onKeyUp={this.props.onKeyUp}
          />
        )}
      </KeytipData>
    );
  }

  private keytipExecute = (executeTarget: HTMLElement) => {
    defaultKeytipExecute(executeTarget);
  };

  public componentDidMount() {
    const toolTip = this.props.customTooltip || (this.props.hideLabel && this.props.label);
    if (this.context.setTooltipElement && this.context.updateTooltipsHidden && toolTip) {
      this.tooltipHost = new AppTooltipHost({
        updateTooltipsHidden: this.context.updateTooltipsHidden,
        setTooltipElement: this.context.setTooltipElement,
        refElement: this.textField,
        controlId: this.props.id,
        controlLabel: toolTip,
        tooltipDirection: this.props.tooltipDirection,
        performSetOnNextFrame: !!checkExperiment(this.context, ribbonExperiments.delayRenderTooltipsIsEnabled),
      });
      this.tooltipHost.registerTooltipHostActions();
    }
  }

  public componentWillUnmount() {
    if (this.tooltipHost) {
      this.tooltipHost.unregisterTooltipHostActions();
    }
  }

  public componentDidUpdate(prevProps: AppInputProps) {
    const toolTip = this.props.customTooltip || this.props.label;
    if (this.tooltipHost && prevProps.customTooltip !== this.props.customTooltip && toolTip) {
      this.tooltipHost.updateTooltip(toolTip);
    }
  }

  public get value(): string | undefined {
    return this.textField && this.textField.current ? this.textField.current.value : undefined;
  }

  public static getDerivedStateFromProps(nextProps: AppInputProps, state: AppInputState) {
    // There's a gap between when we update the input value with the new selected value where an IME keyboard
    // could add unintended text into the input for a controlled input. To fix this, we set a state that
    // will show the value we submitted until we get the new prop and remove the state value.
    if (nextProps.defaultValue === undefined && state.previousValue !== nextProps.value) {
      return {
        previousValue: nextProps.value,
        text: undefined,
      };
    }
    return null;
  }

  // Resets the state to initial state
  public resetState = () => {
    this.setState({ text: this.initialText });
    this.props.onChange && this.props.onChange(this.initialText);
  };

  public focus = () => {
    if (!this.textField || !this.textField.current) {
      return;
    }

    this.props.selectTextOnFocus ? this.textField.current.select() : this.textField.current.focus();
    this.initialText = this.value;
  };

  public select = () => {
    this.textField.current?.select();
  };

  public blur = () => {
    this.props.onBlur && this.props.onBlur();
    if (this.props.executeOnBlur) {
      this.validateAndExecute();
    }
    this.initialText = undefined;
  };

  private onChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string): void => {
    if (!this.props.readOnly) {
      this.setState({ text: newValue });
      this.props.onChange && this.props.onChange(newValue);
    }
  };

  private onKeyDown = (ev: React.KeyboardEvent) => {
    this.props.onKeyDown && this.props.onKeyDown(ev);

    if (ev.keyCode === KeyCodes.enter && this.props.executeOnEnter) {
      ev.stopPropagation();
      ev.preventDefault();
      this.validateAndExecute();
    }
  };

  private validateAndExecute = () => {
    this.props.onExecute &&
      this.value !== undefined &&
      this.value !== null &&
      this.props.value !== this.value &&
      this.props.onExecute(this.value);
  };
}
